import React, { createContext, useCallback, useContext, useEffect } from 'react';

import type { CodeBlockSuggestion } from '@aline/common';
import { Queries } from '@aline/common';
import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { merge, uniqBy } from 'lodash';
import type { ExtraProps } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { hash } from 'spark-md5';
import { getCMLocationFromReference } from 'src/editor/toolbars/right-panel/ai/shared/getCMLocationFromReference';
import { Apollo } from 'src/gql/generated';
import type { AlineClientEditor } from 'src/layout-engine';
import { useDocContentChangedCount, useMaybeEditor } from 'src/layout-engine';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import { AlineMarkdown, useTypewriter } from './AlineMarkdown';
import { setRedlineStatuses } from './elements/CodeElement/CodeRedlineListElement/redline-state-helpers';
import { collectRedlineStatuses } from './helpers/collectRedlineStatuses';
import type {
  SuggestionsWithStatus,
  SuggestionWithStatus,
} from './helpers/collectSuggestionsWithStatus';
import { collectSuggestionsWithStatus } from './helpers/collectSuggestionsWithStatus';
import { getReferenceFromSuggestion } from './helpers/getReference';
import { rehypeRestoreProperties, rehypeStoreProperties } from './rehype-plugins/rehype-properties';
import { pulsingMarkdownLink, pulsingMarkdownLinkHash } from './remark-plugins/remark-constants';
import { remarkCitation } from './remark-plugins/remarkCitation';
import { remarkCodeBlock } from './remark-plugins/remarkCodeBlock';
import { remarkNodeId } from './remark-plugins/remarkNodeId';
import { remarkTable } from './remark-plugins/remarkTable';
import { counterResetStyles } from '../../shared/components/Citation';
import { normalizeStreamingText } from '../chat-helpers';
import type { AlineChatAdapter } from '../chat-hooks/useAlineChatAdapter';
import { useAlineChatAdapter } from '../chat-hooks/useAlineChatAdapter';
import { SuggestionStatus } from '../chat-types';
import type { ChatAssistantMessage } from '../chat-types';
import type { ChatDocument } from '../ChatPanel/ChatPanelAssistantMessage';

const useStyles = makeStyles()(theme => ({
  root: {
    ...getTypographyForSx(theme.typography.body1),
    ...counterResetStyles,
    // Drop the trailing paragraph's default bottom margin so the message's action icons sit close to the text rather than floating toward the next message.
    '& > p:last-child': {
      marginBottom: 0,
    },
  },
}));

export type MarkdownComponentProps<El extends HTMLElement = HTMLElement> =
  React.ClassAttributes<El> & React.HTMLAttributes<El> & ExtraProps;

const ChatAssistantMessageContext = createContext<ChatAssistantMessage | null>(null);
const ChatThreadIdContext = createContext<UUID | null>(null);
const ChatAdapterContext = createContext<AlineChatAdapter | null>(null);

export function useChatAssistantMessage() {
  const ctx = useContext(ChatAssistantMessageContext);
  if (!ctx) throw new Error('useChatAssistantMessage must be used within ChatPanelMarkdown');
  return ctx;
}

export function useChatThreadId() {
  const ctx = useContext(ChatThreadIdContext);
  if (!ctx) throw new Error('useChatThreadId must be used within ChatPanelMarkdown');
  return ctx;
}

/**
 * Tool approval / output callbacks for the current thread. Provided once per
 * thread by `ChatAdapterProvider` so the underlying agent-thread query and
 * `toUiMessages` recompute run a single time, not once per rendered markdown
 * block.
 */
export function useChatAdapter() {
  const ctx = useContext(ChatAdapterContext);
  if (!ctx) throw new Error('useChatAdapter must be used within ChatAdapterProvider');
  return ctx;
}

/**
 * Resolves the chat adapter once for an entire thread and shares it with every
 * descendant `ChatPanelMarkdown` via context. Mount this at the thread root
 * (the message list) rather than per message or per markdown block.
 */
export function ChatAdapterProvider({
  children,
  threadId,
}: {
  children: React.ReactNode;
  threadId: UUID | null;
}) {
  const chatAdapter = useAlineChatAdapter(threadId);
  return <ChatAdapterContext.Provider value={chatAdapter}>{children}</ChatAdapterContext.Provider>;
}

export type SuggestionAndStatus = {
  status: SuggestionStatus;
  suggestion: CodeBlockSuggestion;
};

type MessageId = string;

/**
 * This is a lookup that takes in a `messageId` and returns a list of
 * suggestions along with their statuses.
 */
export const suggestionsWithStatusVar = makeVar<Record<MessageId, SuggestionsWithStatus>>({});

export function setSuggestionsWithStatus(
  messageId: MessageId,
  suggestionsWithStatus: SuggestionsWithStatus
) {
  const existingSuggestionsWithStatus = suggestionsWithStatusVar();
  const existingMessageSuggestionsWithStatus = existingSuggestionsWithStatus[messageId] ?? [];
  const newObj = merge({}, existingSuggestionsWithStatus, {
    /**
     * Order MATTERS here.  Incoming suggestionsWithStatus MUST be first so we take them most
     * up-to-date suggestion by groupId.
     */
    [messageId]: uniqBy([...suggestionsWithStatus, ...existingMessageSuggestionsWithStatus], item =>
      item.type === 'suggestion' ? item.suggestion.groupId : item.error.id
    ),
  });
  suggestionsWithStatusVar(newObj);
}

export function useGetSuggestionsWithStatus() {
  const suggestionsWithStatus = useReactiveVar(suggestionsWithStatusVar);
  const getSuggestionsWithStatus = useCallback(
    (messageId: MessageId) => {
      const safeSuggestionsWithStatus = suggestionsWithStatus[messageId] ?? [];
      return safeSuggestionsWithStatus.filter(
        (item): item is SuggestionWithStatus => item.type === 'suggestion'
      );
    },
    [suggestionsWithStatus]
  );
  const getSuggestionByGroupId = useCallback(
    (messageId: MessageId, groupId: string) => {
      const messageSuggestionsWithStatus = suggestionsWithStatus[messageId] ?? [];
      return (
        messageSuggestionsWithStatus.find(
          (item): item is SuggestionWithStatus =>
            item.type === 'suggestion' && item.suggestion.groupId === groupId
        ) ?? null
      );
    },
    [suggestionsWithStatus]
  );
  return { getSuggestionByGroupId, getSuggestionsWithStatus, suggestionsWithStatus };
}

export const ChatPanelMarkdown = React.memo(function ChatPanelMarkdown({
  completionText,
  documentInView,
  message,
  threadId,
}: {
  completionText: string;
  documentInView: ChatDocument | null;
  message: ChatAssistantMessage;
  threadId: UUID;
}) {
  const { classes } = useStyles();
  const editor = useMaybeEditor();
  const pluginState = { citationIndex: 0 };
  const { docContentChangedCount } = useDocContentChangedCount();
  const isMessageStreaming = message.status === Apollo.AgentMessageStatus.Streaming;
  const textToRender = useTypewriter({
    isMessageStreaming,
    message: { id: message.id, text: completionText },
  });
  /**
   * Check the TYPEWRITER OUTPUT for incomplete links, not the server input.
   * During typewriter animation, intermediate states like "[Citation](#d" can occur
   * even when the server sent a complete link. We need to detect and handle these as a pulsing link.
   */
  const { isPulsing, text: normalizedText } = normalizeStreamingText(textToRender);
  const markdownToRender = isPulsing
    ? `${normalizedText}[${pulsingMarkdownLink}](${pulsingMarkdownLinkHash})\n`
    : textToRender;

  useEffect(() => {
    // docContentChangedCount is used to cause suggestion state to be re-evaluated when doc content changes.
    if (docContentChangedCount === 0) return;

    const { hasSuggestionListBlock, suggestionsWithStatus } = collectSuggestionsWithStatus(
      completionText,
      {
        getEditStatus: suggestion =>
          getSuggestionStatusFromServerStatus(editor, {
            documentInView,
            serverStatus: message.data.suggestionStates?.[suggestion.groupId]?.status,
            suggestion,
          }),
      }
    );

    /**
     * One set per message, not per block: the collector accumulates entries
     * across every suggestion-list block, so a later block never replaces the
     * earlier blocks' statuses.
     */
    if (hasSuggestionListBlock) setSuggestionsWithStatus(message.id, suggestionsWithStatus);
  }, [
    completionText,
    docContentChangedCount,
    documentInView,
    editor,
    message.data.suggestionStates,
    message.id,
  ]);

  /**
   * Populate redline edits with status into the reactive variable.
   * Similar to suggestions, but for the new redline edit format.
   */
  useEffect(() => {
    // docContentChangedCount is used to cause suggestion state to be re-evaluated when doc content changes.
    if (docContentChangedCount === 0) return;

    const { hasRedlineBlock, statuses } = collectRedlineStatuses(completionText, {
      documentInViewId: documentInView?.id ?? null,
      suggestionStates: message.data.suggestionStates,
    });

    /**
     * One set per message, not per block: the collector accumulates entries
     * across every redline block, so a later block never replaces the earlier
     * blocks' statuses.
     */
    if (hasRedlineBlock) setRedlineStatuses(message.id, statuses);
  }, [
    completionText,
    docContentChangedCount,
    documentInView,
    message.data.suggestionStates,
    message.id,
  ]);

  return (
    <ChatThreadIdContext.Provider value={threadId}>
      <ChatAssistantMessageContext.Provider value={message}>
        <div>
          <AlineMarkdown
            className={classes.root}
            markdownString={markdownToRender}
            rehypePlugins={[rehypeStoreProperties, rehypeRaw, rehypeRestoreProperties]}
            remarkPlugins={[
              /**
               * Assigns a unique ID to all nodes so we can restore hProperties
               * during the rehype pass.
               */
              remarkNodeId,
              /**
               * Extracts original markdown source for table nodes
               */
              remarkTable,
              /**
               * Adds `citationIndex` (an incrementing counter) to `link` nodes
               */
              [remarkCitation, { message, pluginState, threadId }],
              /**
               * This plugin adds `messageId` and `threadId` to all `code` nodes so
               * we don't have to do it individually.
               */
              [
                remarkCodeBlock,
                {
                  isMessageStreaming,
                  messageId: message.id,
                  threadId,
                },
              ],
            ]}
            type="message"
          />
        </div>
      </ChatAssistantMessageContext.Provider>
    </ChatThreadIdContext.Provider>
  );
});

export type {
  ErrorWithStatus,
  SuggestionsWithStatus,
  SuggestionWithStatus,
} from './helpers/collectSuggestionsWithStatus';

export function getSuggestionStatusFromServerStatus(
  editor: AlineClientEditor | null,
  options: {
    documentInView: ChatDocument | null;
    serverStatus: Apollo.MessageSuggestionStatus | undefined;
    suggestion: CodeBlockSuggestion;
  }
): SuggestionStatus {
  const { documentInView, serverStatus, suggestion } = options;

  if (!documentInView || documentInView.id !== suggestion.documentId)
    return SuggestionStatus.DocumentNotInView;

  // Important for serverStatus to come AFTER documentInView check.
  if (serverStatus) return serverStatus;

  switch (documentInView.latestVersion.__typename) {
    case 'DocumentVersion': {
      // Should be impossible.  Editor should be loaded.
      if (!editor) return SuggestionStatus.NotAlineDoc;

      const reference = getReferenceFromSuggestion(editor, suggestion);
      // If cannot find reference in Editor, it likely means the document has been edited.
      if (!reference) return SuggestionStatus.TextChanged;
      // Should be impossible to get here, but just in case.
      if (reference.__typename !== 'AlineLocation') return SuggestionStatus.NotAlineDoc;

      const location = getCMLocationFromReference(editor, reference);
      const textAtLocation = Queries.string(editor, { at: location, voids: true });
      const isTextChanged = hash(textAtLocation) !== suggestion.textHash;

      return isTextChanged ? SuggestionStatus.TextChanged : SuggestionStatus.Pending;
    }
    case 'StaticFileVersion':
      return SuggestionStatus.NotAlineDoc;
  }
}
